import React, { useState } from 'react';
import { theme } from '../../theme';
import { useChatbotScope } from '../../context/ChatbotScopeContext';
import { usePaletteMode } from '../../context/PaletteModeContext';
import { useCategoryMeta } from '../../context/ClassificationSchemaContext';
import { ModeSwapIcon, ThumbUpIcon, ChevronToggleButton } from '../shared';
import { KonversationsaufkommenChart } from './charts/KonversationsaufkommenChart';
import type { ChatbotAnalytics } from '../../services/chatbotApi';

// <purpose>Statistikseite chatbot-analytics centerpiece. Compact steady-state (Selbstlösungsquote
// hero + top-3 topics) that expands SIDEWAYS into a 3-KPI grid + Konversationsaufkommen KW chart.
// A Kundschaft⇄Test flip swaps the dataset AND recolours the whole card into the Neuanalysen
// (mode.rejections) periwinkle theme.</purpose>
// <dependencies>useChatbotScope (scope + theme + flip), usePaletteMode (light/dark rejection palette),
// useCategoryMeta (role-aware chart/category noun), KonversationsaufkommenChart, shared icons.</dependencies>
// <constraints>
//   LAYOUT-STABILITY HARD RULE — the card must NEVER shift surrounding content:
//   • Expand is WIDTH-ONLY (334 → 100%). Details live inside a data-stx-card-reveal wrapper that is
//     ALWAYS overflow:hidden + always rendered; the min-width:520 inner block is clipped when compact.
//     No display toggle, no height animation → the card's Y and height never change on expand.
//   • The TEST-DATEN badge is ALWAYS rendered; only `visibility` toggles (hidden in Kundschaft) so the
//     flip never nudges the card's height.
//   • Every null metric renders "noch nicht erfasst"/"—"; null trend lines are omitted, never faked.
//   • Controlled expand (props) so StatistikSeite can couple this with the H-G-C overlay.
// </constraints>

const HAIR_LIGHT = '#e7eaef';
const CARD_BORDER_LIGHT = '#eef0f4';
const MUTED_LIGHT = '#8a93a5';
const FAINT_LIGHT = '#aeb4c2';

export const ChatbotAnalyticsCard: React.FC<{
  analytics: ChatbotAnalytics; expanded: boolean; onToggleExpanded: () => void;
}> = ({ analytics, expanded, onToggleExpanded }) => {
  const { scope, toggle } = useChatbotScope();
  const { mode: palette } = usePaletteMode();
  const cm = useCategoryMeta();
  const [spinKey, setSpinKey] = useState(0);

  const isTest = scope === 'test';
  const rej = theme.colors.mode.rejections[palette];
  const ink = isTest ? rej.tileText : theme.colors.navy;     // hero figure + topic/KPI names
  const accent = isTest ? rej.fill : theme.colors.navy;      // topic bars
  const cardBg = isTest ? rej.tile : theme.colors.white;
  const cardBorder = isTest ? theme.colors.borderLight : CARD_BORDER_LIGHT;
  const hair = isTest ? theme.colors.borderLight : HAIR_LIGHT;
  const muted = isTest ? rej.tileText : MUTED_LIGHT;
  const faint = isTest ? rej.tileText : FAINT_LIGHT;
  const trendColor = isTest ? rej.tileText : theme.colors.success;
  const bcardBg = theme.colors.white; // KPI/chart tiles stay white — readable on either card bg

  // Null renders as a compact em-dash (matches the card's existing '—' sub-placeholders) so a null
  // metric occupies the SAME height as a value — flipping Kundschaft⇄Test can't jump the layout.
  const pct = (v: number | null) => (v == null ? '—' : `${v}%`);
  const heroNull = analytics.containmentRate == null;

  const kpiTitleStyle: React.CSSProperties = {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.03em', color: muted,
    fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5, transition: 'color 0.35s',
  };
  const kpiValueStyle: React.CSSProperties = { fontSize: 24, fontWeight: 800, color: ink, marginTop: 4, transition: 'color 0.35s' };
  const kpiSubStyle: React.CSSProperties = { fontSize: 9.5, color: faint, marginTop: 4, transition: 'color 0.35s' };
  const tileStyle: React.CSSProperties = {
    background: bcardBg, border: `1px solid ${hair}`, borderRadius: 10, padding: '13px 15px', transition: 'border-color 0.35s',
  };

  return (
    <div
      data-stx-analytics-card
      style={{
        position: 'relative', display: 'flex', boxSizing: 'border-box',
        width: expanded ? '100%' : 334,
        background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 16,
        boxShadow: theme.shadows.cardLifted, padding: '20px 22px 22px',
        transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1), background 0.35s ease, border-color 0.35s ease',
      }}
    >
      {/* ── Summary (compact steady-state; fixed 290 width so expand only grows the reveal) ── */}
      <div style={{ position: 'relative', flex: '0 0 auto', width: 290, display: 'flex', flexDirection: 'column' }}>
        {/* Flip: Kundschaft ⇄ Test. Spin accumulates 360° per click; recolours via scope. */}
        <button
          type="button"
          aria-label="Ansicht wechseln (Kundschaft/Test)"
          title="Kundschaft ⇄ Test"
          onClick={() => { setSpinKey(k => k + 1); toggle(); }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.55'; }}
          style={{
            position: 'absolute', top: -2, right: -4, width: 28, height: 28, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            color: ink, opacity: 0.55, zIndex: 2, transition: 'color 0.35s, opacity 0.2s',
          }}
        >
          <span style={{ display: 'inline-flex', transform: `rotate(${spinKey * 360}deg)`, transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)' }}>
            <ModeSwapIcon size={16} />
          </span>
        </button>

        {/* Hero — Selbstlösungsquote (Containment) */}
        <div style={{ textAlign: 'center', padding: '14px 0 6px' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: muted, fontWeight: 600, marginBottom: 12, transition: 'color 0.35s' }}>
            Selbstlösungsquote
          </div>
          <div style={{ fontSize: 74, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 0.85, color: heroNull ? faint : ink, transition: 'color 0.35s' }}>
            {heroNull
              ? '—'
              : <>{analytics.containmentRate}<span style={{ fontSize: '0.4em', fontWeight: 600, verticalAlign: '0.5em' }}>%</span></>}
          </div>
          {heroNull ? (
            // Say the honest "not yet captured" meaning ONCE (here), not repeated in every KPI tile.
            <div style={{ fontSize: 11, color: faint, marginTop: 14, transition: 'color 0.35s' }}>noch nicht erfasst</div>
          ) : (analytics.containmentTrendPp != null && (
            <div style={{ fontSize: 13, color: trendColor, marginTop: 14, transition: 'color 0.35s' }}>
              {`▲ ${analytics.containmentTrendPp} pp ggü. Vorquartal`}
            </div>
          ))}
        </div>

        <div style={{ height: 1, background: hair, margin: '20px 0 14px', transition: 'background 0.35s' }} />

        {/* Top-3 topics — "Worüber Kund:innen sprechen" */}
        <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: faint, fontWeight: 600, marginBottom: 8, transition: 'color 0.35s' }}>
          Worüber Kund:innen sprechen
        </div>
        {analytics.topics.slice(0, 3).map((t, i, arr) => (
          <div
            key={t.key}
            style={{
              display: 'grid', gridTemplateColumns: '150px 1fr 38px', alignItems: 'baseline', gap: 12,
              padding: '6px 0', borderBottom: i < arr.length - 1 ? `1px solid ${hair}` : 'none', transition: 'border-color 0.35s',
            }}
          >
            {/* Backend sends the raw K-code as label; resolve to the role-aware noun FE-side via cm */}
            <span style={{ fontSize: 13, color: ink, transition: 'color 0.35s' }}>{cm.label(t.key)}</span>
            <span style={{ height: 2, background: hair, alignSelf: 'center', position: 'relative', transition: 'background 0.35s' }}>
              <span style={{ position: 'absolute', left: 0, top: 0, height: 2, width: `${t.sharePct}%`, background: accent, opacity: 0.45, transition: 'background 0.35s' }} />
            </span>
            <span style={{ fontSize: 11.5, color: muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums', transition: 'color 0.35s' }}>{t.sharePct}%</span>
          </div>
        ))}

        {/* TEST-DATEN badge — ALWAYS rendered; visibility toggles so the flip never jumps Y */}
        <span
          style={{
            visibility: isTest ? 'visible' : 'hidden', alignSelf: 'center', marginTop: 15, whiteSpace: 'nowrap',
            fontSize: 9, fontWeight: 800, letterSpacing: '0.07em', background: rej.pillOnBg, color: rej.pillOnText,
            borderRadius: 4, padding: '3px 9px',
          }}
        >
          TEST-DATEN · nicht in Kundenzahlen
        </span>
      </div>

      {/* ── Reveal wrapper: overflow-clipped; only its WIDTH grows on expand (no display/height toggle) ── */}
      <div
        data-stx-card-reveal
        style={{
          flex: 1, minWidth: 0, overflow: 'hidden',
          ...(expanded ? { marginLeft: 22, borderLeft: `1px solid ${hair}`, paddingLeft: 22 } : {}),
        }}
      >
        <div id="stx-analytics-details" style={{
          minWidth: 520, display: 'flex', flexDirection: 'column', gap: 12,
          // Ease the details in as the card slides open (the chart is pre-drawn and otherwise just "pops"
          // into view as it's uncovered); the 0.12s delay lets the width-slide lead the fade.
          opacity: expanded ? 1 : 0, transition: 'opacity 0.32s ease 0.12s',
        }}>
          {/* 3-KPI grid: Gespräche · 👍 Hilfreich · Nicht verstanden (KU) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <div style={tileStyle}>
              <div style={kpiTitleStyle}><span>Gespräche</span></div>
              <div style={kpiValueStyle}>{analytics.gespraeche.toLocaleString('de-DE')}</div>
              <div style={kpiSubStyle}>
                {analytics.gespraecheTrendPct != null
                  ? <span style={{ color: trendColor, fontWeight: 700 }}>{`▲${analytics.gespraecheTrendPct}%`}</span>
                  : '—'}
              </div>
            </div>
            <div style={tileStyle}>
              <div style={kpiTitleStyle}><ThumbUpIcon size={12} color={muted} /><span>Hilfreich</span></div>
              <div style={kpiValueStyle}>{pct(analytics.hilfreichRate)}</div>
              <div style={kpiSubStyle}>{`n = ${analytics.hilfreichN ?? '—'}`}</div>
            </div>
            <div style={tileStyle}>
              <div style={kpiTitleStyle}><span>Nicht verstanden</span></div>
              <div style={kpiValueStyle}>{pct(analytics.unclassifiedRate)}</div>
              <div style={kpiSubStyle}>Konfusion (KU)</div>
            </div>
          </div>

          {/* Konversationsaufkommen KW chart (per-category hover breakdown, role-aware via cm) */}
          <div style={{ ...tileStyle, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.03em', color: muted, fontWeight: 700, transition: 'color 0.35s' }}>
              Konversationsaufkommen (KW)
            </div>
            <div style={{ fontSize: 9.5, color: faint, margin: '2px 0 0', transition: 'color 0.35s' }}>
              {`Hover: ${cm.categoryNoun}-Aufschlüsselung — wie beim Ticketaufkommen`}
            </div>
            <KonversationsaufkommenChart data={analytics.konversationsaufkommen} filterHash={scope} />
          </div>
        </div>
      </div>

      {/* Expand chevron — shared chrome, positioned on the right edge */}
      <ChevronToggleButton
        open={expanded}
        onClick={onToggleExpanded}
        ariaLabel={expanded ? 'Details ausblenden' : 'Details anzeigen'}
        ariaControls="stx-analytics-details"
        orientation="horizontal"
        style={{ position: 'absolute', right: -13, top: '50%', transform: 'translateY(-50%)', zIndex: 3 }}
      />
    </div>
  );
};
